// POS customer selector: phone/name search modal + quick-create + debt badge.
// mode="chip"   → compact button/chip in toolbar that opens search modal
// mode="inline" → full inline input with dropdown (used in payment panel)
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiGet, apiPost, apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { X, UserPlus, User, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CustomerQuickCreateModal } from "./customer-quick-create-modal";

interface Customer { id: string; phone: string; name: string; }

interface Props {
  selectedId?: string | null;
  onSelect: (customer: Customer | null) => void;
  mode?: "chip" | "inline";
}

function useDebounce(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function fmtDebt(n: number) {
  if (n === 0) return null;
  const abs = Math.abs(n);
  const short = abs >= 1_000_000
    ? (abs / 1_000_000).toFixed(1).replace(/\.0$/, "") + "tr"
    : (abs / 1_000).toFixed(0) + "k";
  return { label: n > 0 ? `Nợ ${short}` : `Dư ${short}`, isDebt: n > 0 };
}

export function CustomerSelect({ selectedId, onSelect, mode = "chip" }: Props) {
  const [phone, setPhone] = useState("");
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickPhone, setQuickPhone] = useState("");
  const [quickName, setQuickName] = useState("");
  const [quickAddress, setQuickAddress] = useState("");
  const [quickCustomerType, setQuickCustomerType] = useState("retail");
  const [quickError, setQuickError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const modalInputRef = useRef<HTMLInputElement>(null);
  const debouncedPhone = useDebounce(phone, 280);

  const { data: selected } = useQuery({
    queryKey: ["customer", selectedId],
    queryFn: () => apiGet<Customer & { debtBalance?: string }>(`/v1/customers/${selectedId}`),
    enabled: !!selectedId,
  });

  const { data: searchResult } = useQuery({
    queryKey: ["customers", "search", debouncedPhone],
    queryFn: () => apiGet<Customer[]>(`/v1/customers?q=${encodeURIComponent(debouncedPhone)}&limit=8`),
    enabled: debouncedPhone.length >= 3,
  });

  const { data: balData } = useQuery({
    queryKey: ["debts", "balance", selectedId],
    queryFn: async () => {
      const res = await apiClient.get<{ meta: { balance: string } }>(
        `/v1/debts?customer_id=${selectedId}&limit=1`,
      );
      return res.data;
    },
    enabled: !!selectedId,
  });

  const quickMut = useMutation({
    mutationFn: () =>
      apiPost<Customer>("/v1/customers/quick", {
        phone: quickPhone,
        name: quickName,
        address: quickAddress,
        customerType: quickCustomerType,
      }),
    onSuccess: (res) => {
      onSelect(res);
      setPhone("");
      setQuickOpen(false);
      setQuickPhone("");
      setQuickName("");
      setQuickAddress("");
      setQuickCustomerType("retail");
      setOpen(false);
      setModalOpen(false);
    },
    onError: (err: unknown) => setQuickError(translateApiError(err)),
  });

  const balance = balData?.meta?.balance ?? "0";
  const balNum = parseFloat(balance);
  const debt = fmtDebt(balNum);
  const suggestions = searchResult ?? [];

  function handleClear() { onSelect(null); setPhone(""); }
  function handleSelect(c: Customer) {
    onSelect(c);
    setPhone("");
    setOpen(false);
    setModalOpen(false);
  }
  function openQuick() {
    // Prefill the phone from the search box, but it stays editable in the modal.
    setQuickPhone(phone);
    setQuickAddress("");
    setQuickError("");
    setQuickOpen(true);
  }
  function openModal() {
    setPhone("");
    setModalOpen(true);
    setTimeout(() => modalInputRef.current?.focus(), 60);
  }

  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [modalOpen]);

  const resultList = (
    <>
      {suggestions.map((c) => (
        <button
          key={c.id}
          onMouseDown={() => handleSelect(c)}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-accent transition-colors"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
            <User size={14} className="text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{c.name || "Chưa có tên"}</div>
            <div className="text-xs text-muted-foreground font-mono">{c.phone}</div>
          </div>
        </button>
      ))}
      {phone.length >= 3 && (
        <button
          onMouseDown={openQuick}
          className="w-full flex items-center gap-3 border-t px-4 py-2.5 text-sm text-primary hover:bg-accent transition-colors"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <UserPlus size={14} className="text-primary" />
          </div>
          <span>Tạo mới &quot;{phone}&quot;</span>
        </button>
      )}
    </>
  );

  // ── Chip mode: labeled button → search modal ──────────────────────────────
  if (mode === "chip") {
    return (
      <>
        <div className="shrink-0">
          {selected ? (
            <div className="flex items-center gap-1 rounded-lg bg-muted/60 pl-2.5 pr-1 py-1 text-xs">
              <button
                onClick={openModal}
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              >
                <User size={12} className="shrink-0 text-muted-foreground" />
                <span className="max-w-[120px] truncate font-medium">
                  {selected.name || selected.phone}
                </span>
                {debt && (
                  <span className={`font-medium ${debt.isDebt ? "text-destructive" : "text-green-600"}`}>
                    ({debt.label})
                  </span>
                )}
              </button>
              <button
                onClick={handleClear}
                className="ml-0.5 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              onClick={openModal}
            >
              <User size={14} />
              <span className="hidden sm:inline">Khách hàng</span>
              <span className="sm:hidden">KH</span>
            </Button>
          )}
        </div>

        {/* Search modal */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50">
            <div className="w-full max-w-md rounded-xl border bg-card shadow-xl">
              <div className="flex items-center gap-3 border-b p-3">
                <Search size={16} className="shrink-0 text-muted-foreground" />
                <Input
                  ref={modalInputRef}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Tìm theo SĐT hoặc tên khách hàng..."
                  className="border-0 shadow-none focus-visible:ring-0 px-0"
                />
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" onClick={() => setModalOpen(false)}>
                  <X size={16} />
                </Button>
              </div>

              <div className="max-h-[40vh] overflow-auto">
                {phone.length < 3 && (
                  <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                    Nhập ít nhất 3 ký tự để tìm kiếm
                  </p>
                )}
                {phone.length >= 3 && suggestions.length === 0 && (
                  <div className="px-4 py-4 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Không tìm thấy khách hàng</p>
                    <Button size="sm" variant="outline" onClick={openQuick} className="gap-1.5">
                      <UserPlus size={14} />
                      Tạo mới &quot;{phone}&quot;
                    </Button>
                  </div>
                )}
                {phone.length >= 3 && suggestions.length > 0 && resultList}
              </div>
            </div>
          </div>
        )}

        {quickOpen && (
          <CustomerQuickCreateModal
            phone={quickPhone}
            onPhoneChange={setQuickPhone}
            name={quickName}
            onNameChange={setQuickName}
            address={quickAddress}
            onAddressChange={setQuickAddress}
            customerType={quickCustomerType}
            onCustomerTypeChange={setQuickCustomerType}
            error={quickError}
            loading={quickMut.isPending}
            onSubmit={() => quickMut.mutate()}
            onClose={() => setQuickOpen(false)}
          />
        )}
      </>
    );
  }

  // ── Inline mode (original behavior) ───────────────────────────────────────
  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{selected.name || selected.phone}</div>
          <div className="text-xs text-muted-foreground font-mono">{selected.phone}</div>
          {balNum !== 0 && (
            <div className={`text-xs font-medium ${balNum > 0 ? "text-destructive" : "text-green-600"}`}>
              Nợ: {new Intl.NumberFormat("vi-VN").format(Math.abs(balNum))} ₫
            </div>
          )}
        </div>
        <button onClick={handleClear} className="shrink-0 text-muted-foreground hover:text-foreground">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        placeholder="Nhập SĐT hoặc tên KH..."
        value={phone}
        onChange={(e) => { setPhone(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        className="text-sm"
      />

      {open && phone.length >= 3 && (
        <div className="absolute z-40 mt-1 w-full rounded-md border bg-card shadow-lg">
          {resultList}
        </div>
      )}

      {quickOpen && (
        <CustomerQuickCreateModal
          phone={quickPhone}
          onPhoneChange={setQuickPhone}
          name={quickName}
          onNameChange={setQuickName}
          address={quickAddress}
          onAddressChange={setQuickAddress}
          customerType={quickCustomerType}
          onCustomerTypeChange={setQuickCustomerType}
          error={quickError}
          loading={quickMut.isPending}
          onSubmit={() => quickMut.mutate()}
          onClose={() => setQuickOpen(false)}
        />
      )}
    </div>
  );
}
